import * as React from 'react';
import { useState, useEffect } from 'react';
import { Question, QTYPES, ProjectLanguage } from '@nexxt/common/types';
import { i18n } from '../../../../../HOC/i18n';
import Select from 'react-select';
import { get, set, unset, cloneDeep } from 'lodash';
import TextField from '@mui/material/TextField';

interface SystemTextsOverrideSettingsProps {
    question: Question;
    projectLanguages: ProjectLanguage[];
    updateQuestionProp: (propName: string, propValue: any) => void;
}

const QUESION_TYPE_SYSTEM_TEXT_KEYS_MAP = {
    [QTYPES.RAPID_CHOICE]: ['RAPID_CHOICE', 'LABEL.INSTRUCTIONS.RAPID_CHOICE']
};

const getPaths = (o) =>
    Object.entries(o).flatMap(([k, v]) =>
        v && typeof v === 'object' ? getPaths(v).map((p) => `${k}.${p}`) : k
    );

/**
 * Allows editing of any of the system texts for the specified @projectLanguages and @question
 */
const SystemTextsOverrideSettings: React.FC<SystemTextsOverrideSettingsProps> =
    ({ question, projectLanguages, updateQuestionProp }): JSX.Element => {
        const [selectedPath, setSelectedPath] = useState('');
        const [systemTextsOverride, setSystemTextsOverride] = useState({
            enabled: true,
            mappingByLangId: Object.fromEntries(
                projectLanguages.map((lang) => [
                    lang.value,
                    question.systemTextsOverride?.mappingByLangId?.[
                        lang.value
                    ] ?? {}
                ])
            )
        });
        const systemTextKeys =
            QUESION_TYPE_SYSTEM_TEXT_KEYS_MAP[question.type] ?? [];
        const i18nResourceBundle = i18n.getResourceBundle('en');
        const i18nResourceBundlePerProjectLanguage = Object.fromEntries(
            projectLanguages.map((projLang) => [
                projLang.value,
                i18n.getResourceBundle(projLang.value)
            ])
        );
        console.log({ i18nResourceBundlePerProjectLanguage });
        const systemTextSubset = systemTextKeys.reduce(
            (acc, currKey) =>
                set(acc, currKey, get(i18nResourceBundle, currKey)),
            {}
        );
        const systemTextSubsetPaths = getPaths(systemTextSubset);
        const systemTextSubsetPathsAsSelectOptions = systemTextSubsetPaths.map(
            (path) => ({ value: path, label: path, id: path })
        );
        const selectedOption = systemTextSubsetPathsAsSelectOptions.find(
            (o) => o.id == selectedPath
        );

        useEffect(() => {
            updateQuestionProp('systemTextsOverride', systemTextsOverride);
        }, [systemTextsOverride]);

        const onChangeSystemText = (
            langId: string,
            systemTextPath: string,
            overrideValue: string
        ) => {
            const newSystemTextsOverride = cloneDeep(systemTextsOverride);
            if (!overrideValue?.length) {
                unset(
                    newSystemTextsOverride?.mappingByLangId?.[langId],
                    systemTextPath
                );
            } else {
                set(
                    newSystemTextsOverride?.mappingByLangId?.[langId],
                    systemTextPath,
                    overrideValue
                );
            }
            setSystemTextsOverride(newSystemTextsOverride);
        };

        return (
            <div>
                <div className="col-md-12 form-group-header">
                    Override System Texts
                </div>
                <Select
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    size="small"
                    autoFocus={false}
                    options={systemTextSubsetPathsAsSelectOptions}
                    value={selectedOption}
                    noOptionsMessage={() =>
                        'No system texts found for this question.'
                    }
                    onChange={(selectedOption) =>
                        setSelectedPath(selectedOption.id)
                    }
                />
                {selectedOption &&
                    projectLanguages.map((projectLanguage) => (
                        <div className="mt-4 mb-4" key={projectLanguage.label}>
                            <TextField
                                fullWidth
                                label={projectLanguage.label}
                                size="small"
                                placeholder={get(
                                    i18nResourceBundlePerProjectLanguage[
                                        projectLanguage.value
                                    ],
                                    selectedOption.id
                                )}
                                value={
                                    get(
                                        systemTextsOverride?.mappingByLangId?.[
                                            projectLanguage.value
                                        ],
                                        selectedOption.id
                                    ) ?? ''
                                }
                                onChange={(e) =>
                                    onChangeSystemText(
                                        projectLanguage.value,
                                        selectedOption.id,
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{
                                    shrink: true
                                }}
                            />
                        </div>
                    ))}
            </div>
        );
    };

export default SystemTextsOverrideSettings;
